import {
  Section,
  SectionHeader,
  SectionContent,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
  Input,
  Button,
} from '@predoc/cow';
import { Plus } from 'lucide-react';
import { useFormContext } from '@predoc/cow';
import { NestedRequestActionFormFieldProps } from './validation';
import { ProviderFacilityModal } from '../suggested-providers-facility/ProviderFacilityModal';
import { ProviderFacilityList } from './ProviderFacilityList';
import { ProviderFacilityFormData } from '../suggested-providers-facility/validation';

export const Retrievals = () => {
  const { control, getValues, setValue, watch, trigger } = useFormContext();

  // Use watch instead of getValues to trigger re-renders
  const providerFacilities = watch('FULL.retrieveFrom') || [];

  const handleAddProvider = (provider: ProviderFacilityFormData) => {
    // Get fresh current value instead of using stale providerFacilities
    const current = getValues('FULL.retrieveFrom') || [];
    setValue('FULL.retrieveFrom', [...current, provider]);
    // Trigger validation to update form state
    trigger('FULL.retrieveFrom');
  };

  const handleEdit = (
    index: number,
    updatedProvider: ProviderFacilityFormData,
  ) => {
    const current = getValues('FULL.retrieveFrom') || [];
    const updated = [...current];
    updated[index] = { ...current[index], ...updatedProvider };
    setValue('FULL.retrieveFrom', updated);
  };

  const handleRemove = (index: number) => {
    const current = getValues('FULL.retrieveFrom') || [];
    const updated = current.filter(
      (_: ProviderFacilityFormData, i: number) => i !== index,
    );
    setValue('FULL.retrieveFrom', updated);
  };

  return (
    <Section className="bg-white">
      <SectionHeader>
        <h1 className="text-xl font-bold">Provider/Facility Retrieval</h1>
      </SectionHeader>
      <SectionContent>
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              control={control}
              name="FULL.requestFrom"
              render={({
                field,
              }: NestedRequestActionFormFieldProps<'FULL.requestFrom'>) => (
                <FormItem className="space-y-2">
                  <FormLabel>Requesting Provider/Facility *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="ex. Dr. Janet Doe -or- Greenville Medical Group"
                    />
                  </FormControl>
                  <FormMessage />
                  <FormDescription>
                    Doctor or facility requesting the records
                  </FormDescription>
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <FormField
                control={control}
                name="FULL.releasedTo"
                render={({
                  field,
                }: NestedRequestActionFormFieldProps<'FULL.releasedTo'>) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Released To *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="ex. Acme Organization" />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      Doctor or facility receiving the records
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="mb-2 text-sm font-medium">
                Providers/Facilities *
              </h3>
              <div className="text-sm text-gray-600">
                Add Providers/Facilities from whom you want to retrieve records
              </div>
            </div>

            <ProviderFacilityList
              providers={providerFacilities}
              onEdit={handleEdit}
              onRemove={handleRemove}
            />

            <FormField
              control={control}
              name="FULL.retrieveFrom"
              render={() => <FormMessage />}
            />

            <ProviderFacilityModal
              onSubmit={handleAddProvider}
              onAddAnother={handleAddProvider}
            >
              <Button variant="outline" type="button">
                <Plus size={16} />
                Add
              </Button>
            </ProviderFacilityModal>
          </div>
        </div>
      </SectionContent>
    </Section>
  );
};
